import { useState, useEffect, useMemo } from 'react';
import { Filter, X, ChevronRight, Leaf, Beaker, Sparkles, Dumbbell, Search, /* Heart */ } from 'lucide-react';
import type { Producto, Embajador } from '@/types';
import productosData from '@/data/productos.json';
import { lineasProducto } from '@/data/datos';

const iconosLinea: Record<string, React.ElementType> = {
  'FT-ELYM LINE': Leaf,
  'BIENESTAR LINE': Beaker,
  'AVANCELY LINE': Sparkles,
  'VYBES LINE': Dumbbell
};

const descripcionesLinea: Record<string, string> = {
  'FT-ELYM LINE': 'Sistema integral diseñado para limpiar, equilibrar y revitalizar tu organismo. La desintoxicación es el primer paso hacia una vida más saludable.',
  'BIENESTAR LINE': 'Nutrición holística que combina ingredientes naturales para fortalecer tu sistema inmunológico y mejorar tu calidad de vida.',
  'AVANCELY LINE': 'Cuidado personal avanzado con tecnología de vanguardia para rejuvenecer tu piel y realzar tu belleza natural.',
  'VYBES LINE': 'Rendimiento óptimo para atletas y personas activas. Proteínas, aminoácidos y suplementos deportivos de alta calidad.'
};

interface ProductoModalProps {
  producto: Producto | null;
  onClose: () => void;
  ambassador: Embajador | null;
}

function ProductoModal({ producto, onClose, ambassador }: ProductoModalProps) {
  // Prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!producto) return null;

  const whatsappNumber = (ambassador?.whatsapp_number || '524492109730').toString().replace(/\D/g, '');
  const whatsappMessage = encodeURIComponent(`Hola, estoy interesado en el producto ${producto.nombre} (${producto.codigo})`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-3xl overflow-hidden shadow-2xl animate-slide-up flex flex-col">
        {/* Header con botón cerrar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white z-10">
          <h3 className="font-serif text-xl text-[#8abd31]">{producto.nombre}</h3>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Contenido con Scroll Separado */}
        <div className="flex-1 flex flex-col overflow-y-auto md:grid md:grid-cols-2 md:overflow-hidden">
          {/* Columna de Imagen */}
          <div className="bg-gradient-to-br from-[#f8f9f5] to-[#ffe4f0]/30 p-4 md:p-8 flex items-center justify-center">
            <div className="relative">
              <div className="w-48 h-56 md:w-64 md:h-80 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                {producto.imagen && producto.imagen.length > 0 ? (
                  <img
                    src={producto.imagen[0]}
                    alt={producto.nombre}
                    className="max-w-full max-h-full object-contain rounded-2xl"
                  />
                ) : (
                  <span className="text-6xl md:text-8xl">🌿</span> // Placeholder
                )}
              </div>
              <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 px-2 py-1 md:px-3 md:py-1 bg-gradient-to-r from-[#8abd31] to-[#e91e8c] text-white text-xs md:text-sm rounded-full">
                {producto.linea}
              </div>
            </div>
          </div>

          {/* Columna de Contenido */}
          <div className="md:overflow-y-auto flex flex-col h-full">
            <div className="p-4 md:p-8 flex-1">
              <div className="space-y-6">
                <div>
                  <h2 className="font-serif text-3xl text-[#8abd31] mb-2">{producto.nombre}</h2>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                    <span>{producto.presentacion}</span>
                    <span>•</span>
                    <span>Código: {producto.codigo}</span>
                    <span>•</span>
                    <span>PVN: {producto.pvn}</span>
                  </div>
                </div>

                {/* Características */}
                <div className="flex flex-wrap gap-2">
                  {producto.caracteristicas.map((caracteristica, idx) => (
                    <span
                      key={caracteristica}
                      className={`px-3 py-1 text-sm rounded-full ${
                        idx % 2 === 0 
                          ? 'bg-[#8abd31]/10 text-[#8abd31]' 
                          : 'bg-[#e91e8c]/10 text-[#e91e8c]'
                      }`}
                    >
                      {caracteristica}
                    </span>
                  ))}
                </div>

                <p className="text-gray-600">{producto.descripcion}</p>

                {/* Beneficios */}
                <div className="bg-[#f8f9f5] rounded-xl p-4">
                  <h4 className="font-medium text-[#8abd31] mb-2 flex items-center gap-2">
                    {/* <Sparkles className="w-4 h-4 text-[#8abd31]" /> */}
                    Beneficios
                  </h4>
                  <p className="text-sm text-gray-600">{producto.beneficios}</p>
                </div>

                {/* Ingredientes */}
                <div>
                  <h4 className="font-medium text-[#8abd31] mb-2">Ingredientes principales</h4>
                  <div className="flex flex-wrap gap-2">
                    {producto.ingredientes.slice(0, 12).map((ingrediente) => (
                      <span
                        key={ingrediente}
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-lg"
                      >
                        {ingrediente}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hábito Elym */}
                {producto.habitos_elym && (
                  <div className="bg-[#e91e8c]/5 rounded-xl p-4 border border-[#e91e8c]/10">
                    <h4 className="font-medium text-[#e91e8c] mb-2">{/* 💡 */} Hábito Elym</h4>
                    <p className="text-sm text-gray-600">{producto.habitos_elym}</p>
                  </div>
                )}

                {/* Modo de empleo */}
                <div>
                  <h4 className="font-medium text-[#8abd31] mb-2">Modo de empleo</h4>
                  <p className="text-sm text-gray-600">{producto.modo_empleo}</p>
                </div>

                {/* Tratamientos */}
                {producto.tratamientos.length > 0 && producto.tratamientos[0] !== '' && (
                  <div>
                    <h4 className="font-medium text-[#8abd31] mb-2 flex items-center gap-2">
                      {/* <Heart className="w-4 h-4 text-[#e91e8c]" /> */}
                      Tratamientos recomendados
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {producto.tratamientos.slice(0, 8).map((tratamiento, idx) => (
                        <span
                          key={tratamiento}
                          className={`px-3 py-1 text-sm rounded-full ${
                            idx % 2 === 0 
                              ? 'bg-[#e91e8c]/10 text-[#e91e8c]' 
                              : 'bg-[#8abd31]/10 text-[#8abd31]'
                          }`}
                        >
                          {tratamiento}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Precio y CTA */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm p-4 border-t border-gray-100 md:static md:bg-transparent md:border-0 md:p-8 md:pt-0 z-20">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-gray-500">Precio</div>
                  <div className="text-2xl md:text-3xl font-serif font-bold text-[#e91e8c]">{producto.precio}</div>
                </div>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-accent w-full sm:w-auto text-center shadow-lg shadow-[#e91e8c]/30"
                >
                  Contactar Vendedor
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CatalogoProps {
  initialLinea?: string | null;
  initialProducto?: Producto | null;
  ambassador: Embajador | null;
}

export default function Catalogo({ initialLinea, initialProducto, ambassador }: CatalogoProps) {
  const [lineaActiva, setLineaActiva] = useState('todos');
  const [productoSeleccionado, setProductoSeleccionado] = useState<Producto | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const productos = useMemo(() => productosData as Producto[], []);

  useEffect(() => {
    if (initialLinea) {
      setLineaActiva(initialLinea);
    }
    if (initialProducto) {
      setProductoSeleccionado(initialProducto);
    }
  }, [initialLinea, initialProducto]);

  const productosFiltrados = useMemo(() => {
    let filtrados = productos;

    if (lineaActiva !== 'todos') {
      filtrados = filtrados.filter((p) => p.linea === lineaActiva);
    }

    if (busqueda) {
      const query = busqueda.toLowerCase();
      filtrados = filtrados.filter(
        (p) =>
          p.nombre.toLowerCase().includes(query) ||
          p.descripcion.toLowerCase().includes(query) ||
          p.tratamientos.some((t) => t.toLowerCase().includes(query))
      );
    }

    return filtrados;
  }, [productos, lineaActiva, busqueda]);

  const handleLineaChange = (linea: string) => {
    setLineaActiva(linea);
  };

  const productosPorLinea = useMemo(() => {
    const agrupados: Record<string, Producto[]> = {};
    productosFiltrados.forEach((p) => {
      if (!agrupados[p.linea]) {
        agrupados[p.linea] = [];
      }
      agrupados[p.linea].push(p);
    });
    return agrupados;
  }, [productosFiltrados]);

  return (
    <div className="min-h-screen bg-[#f8f9f5] pt-24">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#8abd31] to-[#8abd31]">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
                  Catálogo de Productos
                </h1>
                <p className="text-white/70 max-w-xl">
                  Descubre nuestra línea de suplementos 100% orgánicos, diseñados para tu bienestar integral.
                </p>
              </div>

              {/* Search */}
              <div className="relative w-full md:w-80 flex items-center group">
                <Search 
                  className={`absolute left-4 w-5 h-5 z-10 transition-colors duration-300 ${
                    isSearchFocused ? 'text-[#e91e8c]' : 'text-gray-700'
                  }`} 
                  strokeWidth={2.5}
                />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  placeholder="Buscar productos..."
                  className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white placeholder:text-white/50 outline-none focus:bg-white/20 transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-20 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto py-4">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
              <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
              {lineasProducto.map((linea) => (
                <button
                  key={linea.id}
                  onClick={() => handleLineaChange(linea.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    lineaActiva === linea.id
                      ? 'bg-[#8abd31] text-white shadow-lg shadow-[#8abd31]/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {linea.nombre}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid - SkyHarvest Style */}
      <section className="py-16">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto space-y-20">
            {Object.entries(productosPorLinea).map(([linea, productosLinea]) => {
              const Icono = iconosLinea[linea] || Leaf;
              return (
                <div key={linea} className="space-y-8">
                  {/* Line Header */}
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#8abd31] to-[#8abd31] rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Icono className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="font-serif text-3xl text-[#8abd31] mb-2">{linea}</h2>
                      <p className="text-gray-600 max-w-2xl">
                        {descripcionesLinea[linea] || 'Línea de productos premium Ismerely'}
                      </p>
                    </div>
                  </div>

                  {/* Products Grid */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {productosLinea.map((producto) => (
                      <div
                        key={producto.codigo}
                        onClick={() => setProductoSeleccionado(producto)}
                        className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer"
                      >
                        {/* Image */}
                        <div className="aspect-[4/5] bg-gradient-to-br from-[#f8f9f5] to-[#e8efe9] flex items-center justify-center relative overflow-hidden">
                          {producto.imagen && producto.imagen.length > 0 ? (
                            <img 
                              src={producto.imagen[0]}
                              alt={producto.nombre} 
                              className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-110" 
                            />
                          ) : (
                            <div className="text-6xl transition-transform duration-500 group-hover:scale-110">
                              🌿
                            </div>
                          )}
                          
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-[#8abd31]/0 group-hover:bg-[#8abd31]/10 transition-colors duration-300" />
                          
                          {/* Quick View Button */}
                          <div className="absolute bottom-4 left-4 right-4 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                            <button className="w-full py-3 bg-white text-[#8abd31] rounded-full font-medium shadow-lg hover:bg-[#8abd31] hover:text-white transition-colors">
                              Ver detalles
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <div className="text-xs text-[#8abd31] font-medium mb-1">
                            {producto.linea}
                          </div>
                          <h3 className="font-serif text-lg text-[#8abd31] mb-2 line-clamp-1 group-hover:text-[#8abd31] transition-colors">
                            {producto.nombre}
                          </h3>
                          <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                            {producto.descripcion}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="font-serif text-xl font-bold text-[#8abd31]">
                              {producto.precio.split('/')[0]}
                            </span>
                            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#8abd31] group-hover:translate-x-1 transition-all" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {productosFiltrados.length === 0 && (
              <div className="text-center py-16">
                {/* <div className="text-6xl mb-4">🔍</div> */}
                <h3 className="font-serif text-2xl text-[#8abd31] mb-2">                  No se encontraron productos
                </h3>
                <p className="text-gray-500">
                  Intenta con otra búsqueda o selecciona otra categoría
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Product Modal */}
      {productoSeleccionado && (
        <ProductoModal
          producto={productoSeleccionado}
          onClose={() => setProductoSeleccionado(null)}
          ambassador={ambassador}
        />
      )}
    </div>
  );
}
